/**
 * @file JWTCookieManager.tsx
 * @description Component to manage JWT cookies for NodeBB session sharing (controlled by feature flags)
 */

"use client";

import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { setJWTCookie, clearJWTCookie, getJWTFromCookie } from "@/lib/utils/jwtCookie";
import { useFeatureFlagsWithFallback } from "@/lib/featureFlags";
import { clientLogger } from "@/lib/logger-client";

export function JWTCookieManager() {
  const { data: session, status } = useSession();
  const [isSettingCookie, setIsSettingCookie] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const { nodeBBIntegration } = useFeatureFlagsWithFallback();

  useEffect(() => {
    const currentUserId = session?.user?.id;
    
    // Detect account switching
    const isAccountSwitch = currentUserId && lastUserId && currentUserId !== lastUserId;
    
    if (isAccountSwitch) {
      // Clear old JWT immediately
      clearJWTCookie();
      
      // Force regeneration by clearing stored JWT in database
      fetch('/api/auth/invalidate-nodebb-jwt', { method: 'POST' })
        .catch(error => clientLogger.error('[JWT Cookie] Error invalidating old JWT', { error }));
      
      // Reset initialization to allow new JWT generation
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasInitialized(false);
      setIsSettingCookie(false);
    }
    
    // Update last user ID
    if (currentUserId && currentUserId !== lastUserId) {
      setLastUserId(currentUserId);
    }
    
    // Only run once per account session to prevent infinite loops
    if (hasInitialized && !isAccountSwitch) {
      return;
    }
    
    // Only manage cookies if NodeBB integration is enabled
    if (!nodeBBIntegration) {
      setHasInitialized(true);
      return;
    }
    
    const handleJWTCookie = async () => {
      if (status === "loading") {
        return;
      }

      if (status === "authenticated" && session?.user?.id) {
        
        // User is logged in - ensure they have a valid JWT cookie
        const existingCookie = getJWTFromCookie();
        let isCookieValid = false;
        
        if (existingCookie) {
          try {
            const decoded = decodeJWTPayload(existingCookie);
            if (
              decoded &&
              decoded.id === session.user.id &&
              decoded.email === session.user.email &&
              decoded.exp &&
              decoded.exp > Math.floor(Date.now() / 1000)
            ) {
              isCookieValid = true;
            }
          } catch (e) {
            isCookieValid = false;
          }
        }
        
        if (!isCookieValid && !isSettingCookie) {
          setIsSettingCookie(true);
          
          try {
            // First, try to get existing JWT from Firestore
            let response = await fetch('/api/auth/get-nodebb-jwt');
            let data = await response.json();
            
            // If no JWT exists, generate one
            if (!data.jwt && session.user.email && session.user.name) {
              const generateResponse = await fetch('/api/auth/generate-nodebb-jwt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user: {
                    id: session.user.id,
                    email: session.user.email,
                    name: session.user.name,
                    image: session.user.image
                  }
                })
              });
              
              if (generateResponse.ok) {
                const generateData = await generateResponse.json();
                data.jwt = generateData.jwt;
              } else {
                const errorText = await generateResponse.text();
                clientLogger.error('[JWT Cookie] Failed to generate JWT', { errorText });
              }
            }
            
            if (data.jwt) {
              setJWTCookie(data.jwt);
            }
          } catch (error) {
            clientLogger.error('[JWT Cookie] Error setting JWT cookie', { error });
          } finally {
            setIsSettingCookie(false);
            setHasInitialized(true); // Mark as initialized
          }
        } else if (isCookieValid) {
          setHasInitialized(true);
        } else if (isSettingCookie) {
        }
      } else if (status === "unauthenticated") {
        const existingCookie = getJWTFromCookie();
        if (existingCookie) {
          clearJWTCookie();
        }
        setHasInitialized(true);
      }
    };

    handleJWTCookie();
  }, [session, status, nodeBBIntegration, hasInitialized, lastUserId, isSettingCookie]);

  // This component doesn't render anything
  return null;
}

/**
 * Decodes the payload of a JWT token on the client side without signature validation.
 */
function decodeJWTPayload(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

